import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Star, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { usePhoneBar } from "@/contexts/PhoneBarContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import safewayLogoColor from "@/assets/safeway-roofing-siding-logo.png";
import adsHero from "@/assets/ads-hero-roofing.jpg";

export const AdsHeroV2 = () => {
  const { phoneNumber } = usePhoneBar();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    zip: "",
    name: "",
    phone: "",
    email: "",
  });

  const handleZipSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.zip.length >= 5) setStep(2);
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-contact-form", {
        body: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: "residential-roofing",
          message: `Zip: ${formData.zip} — From ads landing page`,
        },
      });
      if (error) throw error;
      toast({ title: "Thank You!", description: "We'll contact you shortly for your free estimate." });
      setFormData({ zip: "", name: "", phone: "", email: "" });
      setStep(1);
    } catch {
      toast({ title: "Error", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedPhone = phoneNumber.replace("+1", "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

  return (
    <section className="w-full">
      <div className="grid lg:grid-cols-2 min-h-[600px]">
        {/* Left — Image */}
        <div className="relative hidden lg:block">
          <img src={adsHero} alt="Professional roofing project" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
          <div className="absolute bottom-8 left-8 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-lg px-4 py-3">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="text-white text-sm font-medium">Licensed & Insured Since 2007</span>
          </div>
        </div>

        {/* Right — Form */}
        <div className="flex flex-col justify-center px-6 py-12 lg:px-16 lg:py-16 bg-background">
          <img src={safewayLogoColor} alt="Safeway Roofing" className="h-14 w-auto object-contain self-start mb-8" />

          {/* Google Stars Badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-secondary text-secondary" />
              ))}
            </div>
            <span className="text-sm text-muted-foreground font-medium">4.9 Stars · 500+ Reviews</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-3">
            Long Island's #1 Rated<br />
            <span className="text-primary">Roofing Contractor</span>
          </h1>
          <p className="text-muted-foreground mb-8 max-w-md">
            Get a free, no-obligation roof inspection and estimate. GAF Master Elite certified — top 2% of roofers nationwide.
          </p>

          {/* Mobile hero image */}
          <div className="lg:hidden relative rounded-xl overflow-hidden mb-8 aspect-video">
            <img src={adsHero} alt="Professional roofing project" className="w-full h-full object-cover" />
          </div>

          {/* Multi-Step Form */}
          {step === 1 ? (
            <form onSubmit={handleZipSubmit} className="space-y-4 max-w-sm">
              <label className="text-sm font-semibold text-foreground">Enter your zip code to get started</label>
              <div className="flex gap-2">
                <Input
                  placeholder="ZIP Code"
                  value={formData.zip}
                  onChange={(e) => setFormData((p) => ({ ...p, zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                  className="text-lg h-12"
                  required
                  minLength={5}
                  maxLength={5}
                  inputMode="numeric"
                  aria-label="ZIP Code"
                />
                <Button type="submit" size="lg" className="h-12 px-6 font-semibold">
                  Start <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleFinalSubmit} className="space-y-3 max-w-sm">
              <p className="text-sm text-muted-foreground">
                ZIP: <strong>{formData.zip}</strong>{" "}
                <button type="button" onClick={() => setStep(1)} className="text-primary underline text-xs">Change</button>
              </p>
              <Input
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                className="h-11"
                aria-label="Full Name"
              />
              <Input
                placeholder="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                required
                className="h-11"
                aria-label="Phone Number"
              />
              <Input
                placeholder="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                required
                className="h-11"
                aria-label="Email Address"
              />
              <Button type="submit" disabled={isSubmitting} className="w-full h-12 font-bold text-base">
                {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Get My Free Estimate"}
              </Button>
            </form>
          )}

          <p className="text-xs text-muted-foreground mt-4">
            Or call us now:{" "}
            <a href={`tel:${phoneNumber}`} className="text-primary font-bold hover:underline">
              {formattedPhone}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
